import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useFilter } from "~/providers/FilterProvider";
import type { ISavedFilter } from "~/types/savedFilters";

interface ISavedFiltersProviderProps {
  children: ReactNode;
}

interface ISavedFiltersContextValue {
  savedFilters: ISavedFilter[];
  setSavedFilters: (value: ISavedFilter[]) => void;
  currentSavedFilter: ISavedFilter | null;
  setCurrentSavedFilter: (value: ISavedFilter | null) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  isFetching: boolean;
  setIsFetching: (value: boolean) => void;
}

export const SavedFiltersContext =
  createContext<ISavedFiltersContextValue | null>(null);

export const SavedFiltersProvider = ({
  children,
}: ISavedFiltersProviderProps) => {
  const [savedFilters, setSavedFilters] = useState<ISavedFilter[]>([]);
  const [currentSavedFilter, setCurrentSavedFilter] =
    useState<ISavedFilter | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const { registerOnClearAllFilters } = useFilter();

  useEffect(
    () =>
      registerOnClearAllFilters(() => {
        setCurrentSavedFilter(null);
      }),
    [registerOnClearAllFilters]
  );

  const value = useMemo(
    () => ({
      savedFilters,
      setSavedFilters,
      currentSavedFilter,
      setCurrentSavedFilter,
      isLoading,
      setIsLoading,
      isFetching,
      setIsFetching,
    }),
    [savedFilters, currentSavedFilter, isLoading, isFetching]
  );

  return (
    <SavedFiltersContext.Provider value={value}>
      {children}
    </SavedFiltersContext.Provider>
  );
};

export const useSavedFilters = (): ISavedFiltersContextValue => {
  const context = useContext(SavedFiltersContext);

  if (!context) {
    throw new Error("useSavedFilters must be used within SavedFiltersProvider");
  }

  return context;
};
